import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SiteSelection } from '~components/Order/SiteSelection/SiteSelection';
import Cycle from '~pages/Sales/Orders/NewOrderForm/Cycle';
import MovingSiteSyncCheckbox from '~pages/Sales/Orders/NewOrderForm/MovingSiteSyncCheckbox';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/**
 * Renders the location card (title-less, overflow visible). Render-only:
 * the geofence field beside the section label (the legacy
 * siteSelectionBlock header), the embedded SiteSelection wired to the
 * Provider's display state and site-change callbacks, the embedded
 * MovingSiteSyncCheckbox (keeps its own PaverTracker gate), the embedded
 * Cycle, and the waypoint contact sections behind their PseudoLink toggles.
 * All state and the change/toggle callbacks live on context; the
 * site-contact autofill and waypoint validation/identity effects are
 * effects/useWaypointContactAutofill + effects/useWaypointValidationSync.
 */
export function LocationDetailsPanel(): JSX.Element {
  const {
    defaultOrder,
    dropOffContacts,
    dropOffWaypoint,
    lastSelectedDropOffPhone,
    lastSelectedPickUpPhone,
    onDefaultSiteAdded,
    onDropOffSiteChange,
    onPickUpSiteChange,
    onStagingSiteChange,
    onTempDefaultSiteRemoved,
    pickUpContacts,
    pickUpWaypoint,
    projectData,
    setLastSelectedDropOffPhone,
    setLastSelectedPickUpPhone,
    showDropOffContact,
    showPickUpContact,
    stagingWaypoint,
    toggleOffDropOffContact,
    toggleOffPickUpContact,
    toggleOnDropOffContact,
    toggleOnPickUpContact,
    watchedCompany,
    watchedDropOffWaypointSite,
    watchedPickUpWaypointSite,
    watchedProject,
  } = useOrderFormV3Context();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<OrderFormV3FieldValues>();
  // PhoneCodeFlagInput declares `control: Control | undefined` and
  // `errors: FieldErrors<FieldValues>` (the RHF defaults) — legacy bridges
  // the gap with an `as unknown` double-cast, which a lint rule banning
  // type casts in this directory forbids here. The bare useFormContext()
  // call returns the SAME form context under its FieldValues-generic view,
  // which matches the widget's declared contract with zero casts.
  const { control: fieldValuesControl, formState: fieldValuesFormState } =
    useFormContext();

  return (
    <Card sx={{ p: 2, mb: 2, overflow: 'visible' }}>
      {/* Legacy siteSelectionBlock header: section label + inline geofence
          field. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="form-section-title">
          {t('order.form.location_details')}
        </Typography>
        <TextFormField
          control={control}
          isRequired={false}
          name="geofenceDelaySeconds"
          type="number"
          label={t('form_fields.geo_fence_delay')}
          errors={errors}
          sx={{ flex: 1, textWrap: 'nowrap', maxWidth: '213px' }}
          inputProps={{
            style: { fontSize: '12px' },
            'data-test-id': 'order-form-v3-geofence-delay-seconds',
          }}
          isInline
        />
      </Box>

      <SiteSelection
        allowRemoveDefaultSites
        errors={errors}
        companyId={watchedCompany?.id}
        allowSuggestions={true}
        pickUpSite={pickUpWaypoint?.site}
        dropOffSite={dropOffWaypoint?.site}
        stagingSite={stagingWaypoint?.site}
        projectId={watchedProject?.id}
        project={projectData ?? null}
        orderId={defaultOrder?.id}
        onlyShowDefaultSiteMarkers
        onDefaultSiteAdded={onDefaultSiteAdded}
        onTempDefaultSiteRemoved={onTempDefaultSiteRemoved}
        onPickUpSiteChange={onPickUpSiteChange}
        onDropOffSiteChange={onDropOffSiteChange}
        onStagingSiteChange={onStagingSiteChange}
      />

      {/* Legacy renders the telematics sync checkbox at the tail of the
          siteSelectionBlock, right after SiteSelection. The embed keeps its
          own PaverTracker-connected gate and form binding. */}
      <MovingSiteSyncCheckbox />

      <Box sx={{ mt: 2 }}>
        <Cycle forOrderFormV2 project={projectData ?? undefined} />
      </Box>

      {(watchedPickUpWaypointSite || watchedDropOffWaypointSite) && (
        <Divider sx={{ my: 2 }} />
      )}

      {/* Legacy waypointsContactsBlock. */}
      {!showPickUpContact && watchedPickUpWaypointSite && (
        <PseudoLink
          title={`+ ${t('order.form.add_pick_up_site_contact')}`}
          action={toggleOnPickUpContact}
          sx={{ display: 'inline-block' }}
        />
      )}

      {watchedPickUpWaypointSite && (
        <Collapse in={showPickUpContact}>
          <Grid container spacing={2}>
            <Grid
              size={{ xs: 6 }}
              data-test-id="order-form-v3-pick-up-way-point-contact-name"
            >
              <AutocompleteFormField
                control={control}
                name="pickUpWayPoint.contact.name"
                errors={errors}
                sx={{ mr: 2 }}
                list={pickUpContacts}
                label={t('form_fields.name')}
                getValue={(item) => item?.name || item}
                getLabel={(item) => item?.name || item || ''}
                clearable={true}
                disabled={!watchedCompany?.id}
                isRequired={false}
                freeSolo
                onSelect={(item) => {
                  setValue('pickUpWayPoint.contact.name', item?.name || item || '', {
                    shouldDirty: false,
                  });
                  setValue('pickUpWayPoint.contact.phone', item?.phone || '', {
                    shouldDirty: false,
                  });
                  setLastSelectedPickUpPhone(item?.phone || '');
                }}
              />
            </Grid>

            <Grid
              size={{ xs: 6 }}
              data-test-id="order-form-v3-pick-up-way-point-contact-phone"
            >
              <PhoneCodeFlagInput
                key={`pickup-contact-phone-${showPickUpContact}-${lastSelectedPickUpPhone}`}
                control={fieldValuesControl}
                errors={fieldValuesFormState.errors}
                name="pickUpWayPoint.contact.phone"
                label={t('form_fields.phone')}
                disabled={!watchedCompany?.id}
                isRequired={false}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: -2 }}>
              <PseudoLink
                title={t('order.form.remove_pick_up_site_contact')}
                sx={{ display: 'inline-block' }}
                action={toggleOffPickUpContact}
              />
            </Grid>
          </Grid>
        </Collapse>
      )}

      {!showDropOffContact && watchedDropOffWaypointSite && (
        <PseudoLink
          title={`+ ${t('order.form.add_drop_off_site_contact')}`}
          action={toggleOnDropOffContact}
          sx={{ display: 'inline-block' }}
        />
      )}

      {watchedDropOffWaypointSite && (
        <Collapse in={showDropOffContact}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid
              size={{ xs: 6 }}
              data-test-id="order-form-v3-drop-off-way-point-contact-name"
            >
              <AutocompleteFormField
                control={control}
                name="dropOffWayPoint.contact.name"
                errors={errors}
                sx={{ mr: 2 }}
                list={dropOffContacts}
                label={t('form_fields.name')}
                getValue={(item) => item?.name || item}
                getLabel={(item) => item?.name || item || ''}
                clearable={true}
                disabled={!watchedCompany?.id}
                isRequired={false}
                freeSolo
                onSelect={(item) => {
                  setValue('dropOffWayPoint.contact.name', item?.name || item || '', {
                    shouldDirty: false,
                  });
                  setValue('dropOffWayPoint.contact.phone', item?.phone || '', {
                    shouldDirty: false,
                  });
                  setLastSelectedDropOffPhone(item?.phone || '');
                }}
              />
            </Grid>

            <Grid
              size={{ xs: 6 }}
              data-test-id="order-form-v3-drop-off-way-point-contact-phone"
            >
              <PhoneCodeFlagInput
                key={`dropoff-contact-phone-${showDropOffContact}-${lastSelectedDropOffPhone}`}
                control={fieldValuesControl}
                errors={fieldValuesFormState.errors}
                name="dropOffWayPoint.contact.phone"
                label={t('form_fields.phone')}
                isRequired={false}
                disabled={!watchedCompany?.id}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: -2 }}>
              <PseudoLink
                title={t('order.form.remove_drop_off_site_contact')}
                sx={{ display: 'inline-block' }}
                action={toggleOffDropOffContact}
              />
            </Grid>
          </Grid>
        </Collapse>
      )}
    </Card>
  );
}
